import React, {
  createContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import {
  BrowserRouter,
  useRoutes,
  Route,
  Routes,
  Navigate,
  useNavigate,
} from 'react-router-dom'
import './app.css'
import API, { setGlobalApiErrorHandler } from './services/API'
import Loading from './pages/loading/loadingPage'
import LoginPage from './pages/login/Login.jsx'
import ForgotPasswordPage from './pages/login/ForgotPassword.jsx'
import MainFrame from './components/mainFrame/MainFrame.jsx'
import Theme from './components/Theme.jsx'
import Box from '@mui/material/Box'

export const UserContext = createContext({})
export const TenantSettingsContext = createContext({})
export const GlobalContext = createContext({})

// Change first day of week to Monday
import moment from 'moment'
import RegisterAccountPage from './pages/login/register/Register'
import IDCheck from './pages/login/idcheck/IDCheck'
import VerifyEmail from './pages/login/register/VerifyEmail'
import ErrorBoundary from './components/ErrorBoundary'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import MyApplication from './pages/applications/MyApplication'
import ReferenceRequest from './pages/applications/referenceRequest/ReferenceRequest.jsx'
import PatientFeedback from './pages/governance/feedback/forms/PatientFeedback.jsx'
import AccessRecord from './pages/patientRecords/Sharing/PublicAccess/AccessRecord.jsx'
import ClientPortal from './pages/clients/ClientPortal/ClientPortal.jsx'
import HospitalBookingPortal from './pages/patientTransport/HospitalPortal/HospitalBookingPortal.jsx'
import CRM from './CRM/CRM.jsx'
import UserSessionIdleTimer from './partials/UserSessionIdleTimer.jsx'
import SignUp from './SIGNUP/SignUp.jsx'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { getCurrencySymbol } from './helpers/currency'

// Some tenants have CompanySettings.currency stored as an ISO code ('GBP')
// rather than a symbol; every consumer renders orgSettings.currency raw, so
// normalise it to a symbol once, where settings enter the context.
const normaliseOrgConfig = (data) =>
  data?.orgSettings?.currency
    ? {
        ...data,
        orgSettings: {
          ...data.orgSettings,
          currency: getCurrencySymbol(data.orgSettings.currency),
        },
      }
    : data
import GlobalBanners from './helpers/GlobalBanners.jsx'
import { Stack } from '@mui/material'
import ImpersonateGate from './pages/dev/ImpersonationGate.jsx'
import useCrisp from './helpers/useCrisp.js'
import PublicDocs from './docs/components/PublicDocs.jsx'
import NotFound from './components/NotFound.jsx'
import { docRoutes } from './docs/routes/_config.js'
import BigModal from './components/Modals/BigModal.jsx'
import LegalAgreementModal from './legal/LegalAgreementModal.jsx'
import UpgradeBoundary from './components/UpgradeBoundary.jsx'
import TenantNotFound from './pages/login/invalidTenant/TenantNotFound.jsx'
import PublicRouter from './routes/PublicRouter.js'
import DocumentTitleManager from './helpers/DocumentTitleManager.jsx'
import UpdatesHub from './pages/dev/Updates.jsx'
import DemoExperience from './components/demo/DemoExperience.jsx'

const getBillingContextFields = (subscriptionInfo = {}) => {
  const {
    subscriptionTier,
    stripeSubscriptionStatus,
    stripePaymentGraceUntil,
    stripeLastPaymentSucceededAt,
    stripeSubscriptionExpiry,
    subscriptionCancellationDate,
    stripeSubscriptionId,
    effectiveGraceUntil,
  } = subscriptionInfo

  return {
    subscriptionTier: subscriptionTier ?? 1,
    stripeSubscriptionStatus,
    stripePaymentGraceUntil,
    stripeLastPaymentSucceededAt,
    stripeSubscriptionExpiry,
    subscriptionCancellationDate,
    stripeSubscriptionId,
    effectiveGraceUntil,
  }
}

moment.updateLocale('en', {
  week: {
    dow: 1,
  },
})

const DocsSubdomainRouter = () =>
  useRoutes([
    {
      path: '/',
      element: <PublicDocs />,
      children: docRoutes,
    },
    {
      path: '/docs/updates',
      element: <UpdatesHub />,
    },
    {
      path: '/docs/*',
      element: <Navigate to="/" replace />,
    },
  ])

const App = () => {
  const navigate = useNavigate()
  const subdomain = window.location.hostname.split('.')[0]
  if (subdomain === 'signup' || process.env.SIGNUPMODE === 'true') {
    return <SignUp />
  }
  if (
    subdomain === 'crmadmin' ||
    process.env.REACT_APP_CRMADMINMODE === 'true'
  ) {
    return <CRM />
  } else if (subdomain === 'docs') {
    return (
      <Theme>
        <Box height="100vh" width="100%">
          <DocsSubdomainRouter />
        </Box>
      </Theme>
    )
  } else if (subdomain === 'apicallback') {
    const urlParams = new URLSearchParams(window.location.search)
    const tenantID = urlParams.get('state')
    if (tenantID) {
      window.location.href = `https://${tenantID}.${process.env.ROOT_DOMAIN}${window.location.pathname}${window.location.search}`
      return null
    }
  }
  const [loading, setLoading] = useState(true)
  const [bootstrapError, setBootstrapError] = useState(null)
  const [user, setUser] = useState({})
  const [orgConfig, setOrgConfig] = useState({})
  const [baseLocationOptions, setBaseLocationOptions] = useState([])
  const [toast, setToast] = useState(null)
  const [timeTrackerDisplay, setTimeTrackerDisplay] = useState([])
  const [tenantInfo, setTenantInfo] = useState(null)
  const [upgradeRequired, setUpgradeRequired] = useState(false)
  const [invalidSubdomain, setInvalidSubdomain] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  function getOrgSettings() {
    API.get('/admin/site-configuration')
      .then((res) => {
        setOrgConfig((prev) => ({ ...prev, ...normaliseOrgConfig(res.data) }))
      })
      .catch((e) => console.log(e))
  }

  function getTenantInfo() {
    API.post('/tenant-information').then((res) => {
      setTenantInfo((prev) => ({ ...prev, ...res.data }))
    })
  }

  function getSubscriptionInfo() {
    API.get('/subscription-info').then((res) => {
      setTenantInfo((prev) => ({
        ...prev,
        ...getBillingContextFields(res?.data),
      }))
    })
  }

  useEffect(() => {
    setGlobalApiErrorHandler((error) => {
      const status = error.response?.status
      const data = error.response?.data

      if (status === 403 && data?.errorCode === 'demoUploadsDisabled') {
        setToast({
          severity: 'info',
          message: 'Sorry, document uploads are not available in the demo.',
        })
        return true
      }

      if (status === 403 && data?.errorCode === 'demoActionDisabled') {
        setToast({
          severity: 'info',
          message: 'Sorry, this action is not available in the demo.',
        })
        return true
      }

      if (
        status === 403 &&
        data?.errorCode === 'INSUFFICIENT_SUBSCRIPTION_TIER'
      ) {
        setUpgradeRequired({ ...data, goBack: true })
        return true
      }

      // The package's capacity for employees, vehicles, base locations or
      // concurrently open control rooms has been reached, or a Premium
      // tenant's control-room hours for the month are used (PTS journeys and
      // Enterprise control-room hours are recorded for review only, never
      // limits, so they cannot arrive here; the control-room start / reopen
      // dialogs handle both control-room refusals themselves). The payload
      // carries limitType, used, limit and currentTier so the popup can show
      // the right copy — including the Enterprise+ quote route for tier-3
      // tenants.
      if (status === 403 && data?.errorCode === 'subscriptionLimitReached') {
        setUpgradeRequired({ ...data, limitReached: true, goBack: true })
        return true
      }

      if (status === 500) {
        setErrorMessage(data?.error ?? 'Server error')
        setHasError(true)
        return true
      }

      if (
        status === 404 &&
        (data === 'Tenant not found' ||
          data === 'Tenant not found - identifyTenant')
      ) {
        setInvalidSubdomain(true)
        return true
      }

      if (status === 401 && data?.errorCode === 'noUserSession') {
        setUser({})
        return false
      }

      // The tenant disabled (or archived) this account while it was signed
      // in: the server has destroyed the session, so drop to the login page
      // and say why rather than leaving every request to fail quietly.
      if (status === 401 && data?.errorCode === 'loginDisabled') {
        setToast({
          severity: 'warning',
          message:
            'Login has been disabled for your account. Please contact your administrator.',
        })
        setUser({})
        return true
      }

      if (status === 403 && data?.errorCode === 'TENANT_SUBSCRIPTION_EXPIRED') {
        // Configuration and support areas stay accessible while the
        // subscription is inactive, so don't bounce users off them if a
        // stray blocked endpoint responds while they're there.
        const accessiblePages = [
          '/site-configuration',
          '/sms-history',
          '/sms-credits',
          '/support',
          '/updates',
          '/docs',
          '/billing',
        ]
        const onAccessiblePage = accessiblePages.some((p) =>
          window.location.pathname.startsWith(p),
        )
        if (onAccessiblePage) return true

        if (data?.isSiteAdmin) {
          navigate('/site-configuration/billing')
        } else {
          setUser({})
        }

        return true
      }

      return false
    })

    return () => setGlobalApiErrorHandler(null)
  }, [navigate])

  const isLoggedIn = Object.keys(user).length > 0

  useCrisp(user, tenantInfo, isLoggedIn)

  function getUserAccount() {
    const controller = new AbortController()
    API.get('/account/me')
      .then((res) => {
        if (res.status === 200) {
          setUser(res.data)
        } else if (res.data.errorCode === 'noMatchingTenant') {
          setInvalidSubdomain(true)
        } else {
          setUser({})
        }
        loading && setLoading(false)
      })
      .catch((e) => {
        if (e?.response?.errorCode === 'noMatchingTenant') {
          setInvalidSubdomain(true)
        }
        if (e.response?.status === 401) {
          setUser({})
        } else {
          console.log(e)
        }
      })
      .finally(() => controller.abort())
  }

  useEffect(() => {
    getUserAccount()
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    const bootstrap = async () => {
      try {
        setBootstrapError(null)

        const results = await Promise.allSettled([
          API.get('/account/me', {
            signal: controller.signal,
            timeout: 15000,
          }),
          API.get('/base-location-options', {
            signal: controller.signal,
            timeout: 15000,
          }),
          API.get('/admin/site-configuration', {
            signal: controller.signal,
            timeout: 15000,
          }),
          API.post(
            '/tenant-information',
            {},
            {
              signal: controller.signal,
              timeout: 15000,
            },
          ),
          API.get('/subscription-info', {
            signal: controller.signal,
            timeout: 15000,
          }),
        ])

        const [
          account,
          baseLocations,
          orgSettings,
          tenantInfoRes,
          subscriptionInfo,
        ] = results

        if (account.status === 'fulfilled' && account.value.status === 200) {
          setUser(account.value.data)
        } else {
          setUser({})
        }

        if (
          baseLocations.status === 'fulfilled' &&
          baseLocations.value.status === 200
        ) {
          setBaseLocationOptions(baseLocations.value.data)
        }

        if (
          orgSettings.status === 'fulfilled' &&
          orgSettings.value.status === 200
        ) {
          setOrgConfig((prev) => ({
            ...prev,
            ...normaliseOrgConfig(orgSettings.value.data),
          }))
        }

        if (
          tenantInfoRes.status === 'fulfilled' &&
          tenantInfoRes.value.status === 200
        ) {
          setTenantInfo((prev) => ({ ...prev, ...tenantInfoRes.value.data }))
        }

        if (
          subscriptionInfo.status === 'fulfilled' &&
          subscriptionInfo.value.status === 200
        ) {
          setTenantInfo((prev) => ({
            ...prev,
            ...getBillingContextFields(subscriptionInfo.value?.data),
          }))
        }
      } catch (e) {
        if (e.name === 'CanceledError') return

        if (e.response?.status === 401) {
          setUser({})
        } else {
          console.error('AmbuNet bootstrap failed:', e)
          setBootstrapError(e)
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    bootstrap()

    return () => {
      controller.abort()
    }
  }, [])

  if (bootstrapError) {
    return (
      <Theme>
        <Box
          height="100vh"
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={3}
        >
          <Alert
            severity="warning"
            action={
              <button onClick={() => window.location.reload()}>Retry</button>
            }
          >
            AmbuNet is having trouble loading. Please check your connection and
            try again.
          </Alert>
        </Box>
      </Theme>
    )
  }

  const tenantContextValue = useMemo(
    () => ({ orgConfig, setOrgConfig }),
    [orgConfig], // only recompute if orgConfig changes
  )

  const validTerms =
    isLoggedIn &&
    Number(tenantInfo?.privacyPolicyVersion ?? 0) <=
      Number(user?.legal?.acceptedTermsVersion ?? 0)
  const validPrivacy =
    isLoggedIn &&
    Number(tenantInfo?.termsVersion ?? 0) <=
      Number(user?.legal?.acceptedPrivacyVersion ?? 0)

  // return <Stack gap={2}>
  //     <Alert severity='error' variant='filled' sx={{ borderRadius: 0 }}>We're sorry, but AmbuNet is currently unavailable whilst we perform essential upgrades. If this will affect your operations, please call <a href='tel:03300570620' style={{ color: 'white' }}>0330 057 0620</a>. We expect to resume normal operations by 02:00.</Alert>
  //     <Loading />
  // </Stack>

  if (invalidSubdomain) {
    return <TenantNotFound />
  }

  return (
    <Box height="100vh" width="100%">
      <DocumentTitleManager orgSettings={orgConfig}>
        <GlobalBanners />
        <TenantSettingsContext.Provider
          value={{ ...tenantContextValue, tenantInfo }}
        >
          <ErrorBoundary
            user={user}
            setUser={setUser}
            hasError={hasError}
            errorMessage={errorMessage}
          >
            {loading ? (
              <Loading />
            ) : !isLoggedIn ? (
              <Theme>
                <Box height="100vh" width="100%">
                  <PublicRouter
                    isLoggedIn={isLoggedIn}
                    tenantInfo={tenantInfo}
                    setUser={setUser}
                    setOrgConfig={setOrgConfig}
                  />
                </Box>
              </Theme>
            ) : (
              <Theme>
                <UserContext.Provider value={{ user, setUser }}>
                  <GlobalContext.Provider
                    value={{
                      setToast,
                      timeTrackerDisplay,
                      setTimeTrackerDisplay,
                      getUserAccount,
                      tenantInfo,
                      baseLocationOptions,
                      upgradeRequired,
                      setUpgradeRequired,
                    }}
                  >
                    <UpgradeBoundary
                      upgradeRequired={upgradeRequired}
                      setUpgradeRequired={setUpgradeRequired}
                    >
                      {/* Demo visitors accept the T&Cs as themselves at demo
                        signup — the personas are spoof users who must never
                        be prompted to accept legal documents. */}
                      {!tenantInfo?.isDemo && (
                        <LegalAgreementModal
                          validTerms={tenantInfo?.termsVersion}
                          validPrivacy={tenantInfo?.privacyPolicyVersion}
                          acceptedTermsVersion={
                            user?.legal?.acceptedTermsVersion
                          }
                          acceptedPrivacyVersion={
                            user?.legal?.acceptedPrivacyVersion
                          }
                          setUser={setUser}
                        />
                      )}
                      {tenantInfo?.isDemo && <DemoExperience />}
                      {!user.employee && user.employeeApplication ? (
                        <>
                          <MyApplication />
                          <UserSessionIdleTimer />
                        </>
                      ) : user.clientContact ? (
                        <>
                          <ClientPortal />
                          <UserSessionIdleTimer />
                        </>
                      ) : user.ptsOrg ? (
                        <HospitalBookingPortal />
                      ) : (
                        // Keyed on the header base-location filter: the
                        // server scopes every request by the saved
                        // selection, so changing it must remount the routed
                        // app - every fetch-on-mount screen then refetches
                        // with the new scope instead of showing stale data
                        // until the next navigation.
                        <MainFrame
                          key={(user?.selectedBaseLocations || [])
                            .map(String)
                            .sort()
                            .join(',')}
                        />
                      )}
                      <Snackbar
                        open={!!toast}
                        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                        onClose={() => setToast(null)}
                        autoHideDuration={5000}
                      >
                        {toast && (
                          <Alert
                            severity={toast?.severity ?? 'success'}
                            onClose={() => setToast(null)}
                            sx={{ boxShadow: '0 0 4px #616161' }}
                          >
                            {toast?.message ?? 'Successfully Updated'}
                          </Alert>
                        )}
                      </Snackbar>
                    </UpgradeBoundary>
                  </GlobalContext.Provider>
                </UserContext.Provider>
              </Theme>
            )}
          </ErrorBoundary>
        </TenantSettingsContext.Provider>
      </DocumentTitleManager>
    </Box>
  )
}

export default App
